import type { IconName } from '@circles/tokens';
import { Icon } from '../components/Icon';
import { Badge, EmptyState, OrgLogo, PersonAvatar, cx } from '../components/ui';
import { chatList, selectTab, useStore, type Route, type Tab } from '../state/store';
import { useActiveTheme } from '../state/theme';
import { ChatList, ChatRequests, NewChat } from '../screens/ChatList';
import { ChatInfo, ChatScreen } from '../screens/Chat';
import { ComposePost, FeedScreen, PostDetail } from '../screens/Feed';
import { CallBar, CallView, CallsScreen, IncomingCallToast } from '../screens/Calls';
import { SettingsScreen, YouScreen } from '../screens/You';
import { SignIn } from '../screens/SignIn';
import { useSizeClass } from './adaptive';

const TABS: { id: Tab; label: string; icon: IconName }[] = [
  { id: 'chats', label: 'Chats', icon: 'chats' },
  { id: 'feed', label: 'Feed', icon: 'feed' },
  { id: 'calls', label: 'Calls', icon: 'calls' },
  { id: 'you', label: 'You', icon: 'you' },
];

function renderRoute(route: Route) {
  switch (route.name) {
    case 'chat':
      return <ChatScreen key={route.id} id={route.id} />;
    case 'chatInfo':
      return <ChatInfo id={route.id} />;
    case 'requests':
      return <ChatRequests />;
    case 'newChat':
      return <NewChat />;
    case 'post':
      return <PostDetail key={route.id} id={route.id} />;
    case 'settings':
      return <SettingsScreen key={route.page} page={route.page} />;
  }
}

function TabRoot({ tab }: { tab: Tab }) {
  switch (tab) {
    case 'chats':
      return <ChatList />;
    case 'feed':
      return <FeedScreen />;
    case 'calls':
      return <CallsScreen />;
    case 'you':
      return <YouScreen />;
  }
}

const emptyDetail: Record<Tab, { icon: IconName; title: string; text: string }> = {
  chats: {
    icon: 'chats',
    title: 'Select a chat',
    text: 'Messages are end-to-end encrypted. Drop files here to send them.',
  },
  feed: { icon: 'feed', title: 'Select a post', text: 'Replies open here, next to the timeline.' },
  calls: {
    icon: 'calls',
    title: 'Calls',
    text: 'Start a call from a chat, or pick someone from your recent calls.',
  },
  you: { icon: 'settings', title: 'Settings', text: 'Choose a setting on the left.' },
};

function useTabs() {
  const hasOrg = useStore((s) => s.orgId !== null);
  // Users without an organisation see Chats and Calls only (spec section 2), plus their profile.
  return TABS.filter((t) => hasOrg || t.id !== 'feed');
}

function useUnread() {
  const chats = useStore(chatList);
  const requests = useStore((s) => s.requests.length);
  return chats.filter((c) => (c.unread ?? 0) > 0 && !c.muted).length + requests;
}

export function AppShell() {
  const stage = useStore((s) => s.auth.stage);
  const { wide, platform } = useSizeClass();
  const tab = useStore((s) => s.tab);
  const stack = useStore((s) => s.stacks[s.tab]);
  const call = useStore((s) => s.call);
  const composeOpen = useStore((s) => s.composeOpen);
  const toastMessage = useStore((s) => s.toast);
  const tabs = useTabs();
  const unread = useUnread();
  const theme = useActiveTheme();

  if (stage !== 'in') {
    return (
      <div className="app">
        <SignIn />
      </div>
    );
  }

  const top = stack[stack.length - 1];
  const desktop = platform !== 'ios';
  const showCallFull = call && !call.minimized && !(desktop && call.phase === 'incoming');

  return (
    <div className={cx('app', wide ? 'is-wide' : 'is-compact')}>
      {call && call.minimized && <CallBar call={call} />}
      {wide ? (
        <div className="two-pane">
          <nav className="sidebar" aria-label="Main">
            <div className="sidebar-logo" title={theme.orgName ?? 'Circles'}>
              <OrgLogo size={36} />
            </div>
            {tabs
              .filter((t) => t.id !== 'you')
              .map((t) => (
                <button
                  type="button"
                  key={t.id}
                  className={cx('sidebar-item', tab === t.id && 'is-active')}
                  onClick={() => selectTab(t.id)}
                  aria-label={t.label}
                  title={t.label}
                >
                  <Icon
                    name={t.icon}
                    size={24}
                    filled={false}
                    strokeWidth={tab === t.id ? 2.1 : 1.7}
                  />
                  <span className="sidebar-label">{t.label}</span>
                  {t.id === 'chats' && unread > 0 && <Badge count={unread} />}
                </button>
              ))}
            <span className="sidebar-spacer" />
            <button
              type="button"
              className={cx('sidebar-item', tab === 'you' && 'is-active')}
              onClick={() => selectTab('you')}
              aria-label="You"
              title="You and settings"
            >
              <PersonAvatar id="me" size={32} />
            </button>
          </nav>
          <section className="list-pane">
            <TabRoot tab={tab} />
          </section>
          <section className="detail-pane">
            {top ? renderRoute(top) : <EmptyState {...emptyDetail[tab]} />}
          </section>
        </div>
      ) : (
        <>
          <div className="stack">{top ? renderRoute(top) : <TabRoot tab={tab} />}</div>
          {!top && (
            <nav className="tab-bar" aria-label="Main">
              {tabs.map((t) => (
                <button
                  type="button"
                  key={t.id}
                  className={cx('tab', tab === t.id && 'is-active')}
                  onClick={() => selectTab(t.id)}
                >
                  <span className="tab-icon">
                    <Icon name={t.icon} size={24} strokeWidth={tab === t.id ? 2.1 : 1.7} />
                    {t.id === 'chats' && unread > 0 && <Badge count={unread} />}
                  </span>
                  <span className="tab-label">{t.label}</span>
                </button>
              ))}
            </nav>
          )}
        </>
      )}

      {composeOpen && <ComposePost />}
      {showCallFull && (
        <div className={cx('call-layer', desktop && 'is-desktop')}>
          <CallView call={call} presentation={desktop ? 'window' : 'fullscreen'} />
        </div>
      )}
      {call && desktop && call.phase === 'incoming' && <IncomingCallToast call={call} />}
      {toastMessage && <div className="toast">{toastMessage}</div>}
    </div>
  );
}
